/**
 * Reading the OS clipboard as plain text for the Ctrl/Cmd-Shift-V "paste without formatting" path.
 *
 * The web default is the async Clipboard API (`navigator.clipboard.readText`). On macOS WKWebView that
 * pops the system "Paste" permission menu instead of pasting — the reported "Ctrl-Shift-V just shows a
 * Paste context menu" — so the desktop shell overrides this seam with a native Tauri command that reads
 * the clipboard silently (see setPlainClipboardReader), exactly like the file clipboard/save seams in
 * linkFileActions. When a native reader is installed the chord reads the clipboard directly rather than
 * waiting on a paste event WKWebView never fires for it.
 */
export type PlainClipboardReader = () => Promise<string | null>;

const webReader: PlainClipboardReader = async () => {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return null;
  try {
    return await navigator.clipboard.readText();
  } catch {
    return null; // no Clipboard API, or the read was denied
  }
};

let reader: PlainClipboardReader | null = null;

/** Host app overrides how the clipboard's plain text is read (native, popover-free, on Tauri). */
export function setPlainClipboardReader(fn: PlainClipboardReader): void {
  reader = fn;
}

/** True once a native reader is installed — the desktop chord reads the clipboard directly. */
export function hasNativePlainClipboardReader(): boolean {
  return reader !== null;
}

/** Read the clipboard's plain text via the native reader if installed, else the web Clipboard API. */
export function readPlainClipboard(): Promise<string | null> {
  return (reader ?? webReader)();
}
